// @flow
import React, { Component, Fragment } from 'react'
import styled from 'styled-components'
import _ from 'lodash'
import Modal from '../../FieldSet/Modal'
import Select from '../../controls/Select'
import { translations, translateFunc } from '../../../shared/localize'
import type { Conversion as ConversionType } from '../../types'
import { blueLinkDashed } from '../../AdEdit/controls'
import ErrorText from '../../controls/ErrorText'
import AutoSizeTextArea from 'react-textarea-autosize'
import postJson from '../../../services/postJson'

const t = translateFunc(translations.audience_edit.conversion)

type Errors = Array<string>

type Props = {|
  createConversionPath: string,
  isDisabled: boolean,
  windowOptions: Array<[string, string]>,
  onConversionAdd: (ConversionType) => void
|}

type State = {|
  isModalOpen: boolean,
  newName: string,
  newDomainName: string,
  newUrlMasks: string,
  newWindow: string,
  nameErrors: Errors,
  domainNameErrors: Errors,
  urlMaskErrors: Errors,
  windowErrors: Errors,
  isLoading: boolean
|}

type ErrorResponse = {|
  name?: Errors,
  domain?: Errors,
  url_masks?: Errors,
  window?: Errors
|}

const fieldCommonStyles = `
  width: 100%;
  height: 35px;
  border: 1px solid #ccc;
  border-radius: 5px;
  padding: 0 15px;
  margin: 0 5px 0 0;
  text-align: left;
`

const FieldWrapper = styled.div`
  font-size: 14px;
  padding-bottom: 12px;
`

const TextInput = styled.input`
  ${fieldCommonStyles};
  ${({ hasErrors }: Object) => hasErrors && 'border-color: red;'};
`

const TextArea: typeof AutoSizeTextArea = styled(AutoSizeTextArea)`
  ${fieldCommonStyles};
  padding-top: 10px;
  padding-bottom: 10px;
  ${({ hasErrors }) => hasErrors && 'border-color: red;'};
`

const UrlMasksInstructions = styled.div`
  font-size: 14px;
  line-height: 18px;
  padding-bottom: 8px;
`

const CreateConversionLinkTrigger = styled.a`
  ${blueLinkDashed};
  display: inline-block;
  padding: 0;
  margin: 0;
  ${({ isDisabled }: Object) =>
    isDisabled &&
    `
    color: #aaa;
    border-bottom: none;

    &:hover {
      color: #aaa;
      border-bottom: none;
    }
  `};
`

export default class Form extends Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      isModalOpen: false,
      newName: '',
      newDomainName: '',
      newUrlMasks: '',
      newWindow: this.defaultWindowValue(),
      nameErrors: [],
      domainNameErrors: [],
      urlMaskErrors: [],
      windowErrors: [],
      isLoading: false
    }
  }

  windowChanged(windowValue: string) {
    this.setState({ newWindow: windowValue, windowErrors: [] })
  }

  newUrlMasksChanged(urlMasks: any) {
    this.setState({ newUrlMasks: urlMasks.replace(/\u0020/g, ''), urlMaskErrors: [] })
  }

  createConversionBtnClicked(event: Event) {
    event.preventDefault()
    if (this.props.isDisabled) return
    this.setState({ isModalOpen: true })
  }

  hideModal() {
    this.setState({ isModalOpen: false })
  }

  resetForm() {
    this.setState({
      newName: '',
      newDomainName: '',
      newUrlMasks: '',
      newWindow: this.defaultWindowValue()
    })
  }

  defaultWindowValue() {
    return _.last(this.props.windowOptions)[1]
  }

  onCreateConversionCreate(event: Event) {
    event.preventDefault()

    const { createConversionPath, onConversionAdd } = this.props
    const { newName, newDomainName, newUrlMasks, newWindow } = this.state

    const urlMasks = _.uniq(_.reject(newUrlMasks.split('\n'), _.isEmpty))

    const data = {
      data: {
        name: newName,
        domain: newDomainName,
        url_masks: urlMasks,
        window: newWindow
      }
    }

    this.setState({ isLoading: true })
    postJson(createConversionPath, data)
      .done(({ data: conversion }: Object) => {
        this.hideModal()
        this.resetForm()
        onConversionAdd({
          id: conversion.id,
          name: conversion.name,
          domainName: conversion.domain.name,
          urlMasks: conversion.url_masks,
          window: conversion.window
        })
      })
      .fail(({ responseJSON }: any) => {
        const responseObject: ErrorResponse = responseJSON.message
        this.setState({
          nameErrors: responseObject.name || [],
          domainNameErrors: responseObject.domain || [],
          urlMaskErrors: responseObject.url_masks || [],
          windowErrors: responseObject.window || []
        })
      })
      .always(() => {
        this.setState({ isLoading: false })
      })
  }

  renderModal() {
    const { isModalOpen, newName, newDomainName, newUrlMasks, newWindow } = this.state
    const state = this.state
    const { isDisabled, windowOptions } = this.props

    return (
      <Modal
        maxWidth={500}
        header={t('modal_header')}
        isOpen={isModalOpen}
        hideBtn
        onClose={() => {
          this.hideModal()
        }}
      >
        <FieldWrapper>
          <TextInput
            autoComplete="off"
            value={newName}
            onChange={({ target }) => {
              this.setState({ newName: target.value, nameErrors: [] })
            }}
            placeholder={t('modal_name_field_placeholder')}
          />
          <ErrorText errors={state.nameErrors} />
        </FieldWrapper>

        <FieldWrapper>
          <TextInput
            autoComplete="off"
            value={newDomainName}
            onChange={({ target }) => {
              this.setState({ newDomainName: target.value, domainNameErrors: [] })
            }}
            placeholder={t('modal_domain_name_field_placeholder')}
          />
          <ErrorText errors={state.domainNameErrors} />
        </FieldWrapper>

        <UrlMasksInstructions>{t('modal_url_masks_instructions')}</UrlMasksInstructions>

        <FieldWrapper>
          <TextArea
            minRows={5}
            value={newUrlMasks}
            onChange={({ target }) => {
              this.newUrlMasksChanged(target.value)
            }}
            placeholder={t('modal_url_masks_field_placeholder')}
            className="form-control"
          />
          <ErrorText errors={state.urlMaskErrors} />
        </FieldWrapper>

        <FieldWrapper>
          <Select
            value={newWindow}
            placeholder={t('window_placeholder')}
            options={Select.optionsFromTuples(windowOptions)}
            onChange={(windowValue) => this.windowChanged(windowValue)}
            disabled={isDisabled}
          />
          <ErrorText errors={state.windowErrors} />
        </FieldWrapper>

        <button
          onClick={(event) => {
            this.onCreateConversionCreate(event)
          }}
          disabled={state.isLoading}
          className="blue-button"
        >
          {t('modal_create_btn')}
        </button>
      </Modal>
    )
  }

  render() {
    const { isDisabled } = this.props

    return (
      <Fragment>
        {this.renderModal()}
        <div>
          <CreateConversionLinkTrigger
            isDisabled={isDisabled}
            href="#"
            onClick={(event) => {
              this.createConversionBtnClicked(event)
            }}
          >
            {t('create_conversion_link')}
          </CreateConversionLinkTrigger>
        </div>
      </Fragment>
    )
  }
}
